import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { buildEventListItem } from '@/utils/eventSchema';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  MapPin, 
  ArrowRight, 
  Loader2, 
  Tag,
  Building2,
  Users,
  Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GA Event Tracking
 */
const trackEvent = (eventName, params) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  } else {
    console.log('GA Event:', eventName, params);
  }
};

/**
 * SectorEventsPage - SEO-optimized sector/tag events listing
 * Route: /events/sector/:tagSlug
 */
export default function SectorEventsPage() {
  const { tagSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSectorEvents = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/api/public/sectors/${tagSlug}`);
        setData(response.data);
        
        // Track page view
        trackEvent('seo_page_viewed', {
          page_type: 'sector',
          tag: tagSlug,
          event_count: response.data.total
        });
        
        // Track empty state if no events
        if (!response.data.hasEvents) {
          trackEvent('seo_empty_state_viewed', {
            page_type: 'sector',
            tag: tagSlug
          });
        }
      } catch (err) {
        console.error('Error fetching sector events:', err);
        setError('Unable to load events for this sector.');
      } finally {
        setLoading(false);
      }
    };
    fetchSectorEvents();
  }, [tagSlug]);

  // Generate SEO metadata
  const getPageMeta = () => {
    const sectorName = data?.sector?.name || tagSlug.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
    return {
      title: `${sectorName} Events | Jigsaw Conferences`,
      description: `Discover upcoming ${sectorName} trade shows, conferences and exhibitions across the UK. Find events at ExCeL London, NEC Birmingham, Manchester Central and more.`,
      h1: `${sectorName} Events`
    };
  };

  const pageMeta = getPageMeta();

  // Generate JSON-LD schema
  const generateSchema = () => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": pageMeta.title,
      "description": pageMeta.description,
      "url": `https://events.jigsawconferences.co.uk/events/sector/${tagSlug}`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type": "ListItem", "position": 1, "name": "Events", "item": "https://events.jigsawconferences.co.uk/events"},
          {"@type": "ListItem", "position": 2, "name": data?.sector?.name || tagSlug}
        ]
      }
    };
    
    if (data?.events?.length > 0) {
      schema.mainEntity = {
        "@type": "ItemList",
        "numberOfItems": data.total,
        "itemListElement": data.events.slice(0, 10).map((event, index) => buildEventListItem(event, index + 1))
      };
    }
    
    return schema;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading {tagSlug} events...</span>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEOHead
        title={pageMeta.title}
        description={pageMeta.description}
        url={`https://events.jigsawconferences.co.uk/events/sector/${tagSlug}`}
        schema={generateSchema()}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 to-blue-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="text-purple-200 text-sm mb-4">
            <Link to="/events" className="hover:text-white">Events</Link>
            <span className="mx-2">›</span>
            <span className="text-white">{data?.sector?.name || tagSlug}</span>
          </nav>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-white/20 p-3 rounded-full">
              <Tag className="w-8 h-8" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">{pageMeta.h1}</h1>
          </div>
          
          <p className="text-lg text-purple-100 max-w-2xl">
            {data?.total || 0} upcoming {data?.sector?.name || 'sector'} trade shows, conferences and exhibitions across the UK.
          </p>
          
          {/* Quick stats */}
          <div className="flex flex-wrap gap-4 mt-6">
            <Badge className="bg-white/20 text-white px-4 py-2">
              <Calendar className="w-4 h-4 mr-2" />
              {data?.total || 0} Events
            </Badge>
            <Badge className="bg-white/20 text-white px-4 py-2">
              <MapPin className="w-4 h-4 mr-2" />
              {data?.cities?.length || 0} Cities
            </Badge>
          </div>
        </div>
      </section>

      {/* Cities Filter */}
      {data?.cities?.length > 0 && (
        <section className="bg-gray-50 py-4 border-b">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600 mr-2">Filter by city:</span>
              {data.cities.map(city => (
                <Link key={city.slug} to={`/events/city/${city.slug}/sector/${tagSlug}`}>
                  <Badge variant="outline" className="hover:bg-purple-50 hover:border-purple-500 cursor-pointer">
                    {city.name}
                  </Badge>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Events Grid */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {error ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{error}</p>
            </div>
          ) : !data?.hasEvents ? (
            <div className="text-center py-12">
              <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-700 mb-2">No {data?.sector?.name} events found</h2>
              <p className="text-gray-500 mb-6">We don&apos;t currently have any upcoming events in this sector.</p>
              <Link to="/events">
                <Button className="bg-orange-600 hover:bg-orange-700">
                  Browse All Events
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Upcoming {data.sector.name} Events
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {data.events.map((event) => (
                  <Link
                    key={event.slug}
                    to={`/events/${event.citySlug}/${event.venueSlug}/${event.slug}`}
                    className="block group"
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow border-gray-200 hover:border-purple-500">
                      <CardContent className="p-5">
                        <h3 className="font-semibold text-gray-900 mb-3 group-hover:text-purple-600 line-clamp-2">
                          {event.name}
                        </h3>
                        
                        <div className="space-y-2 mb-3">
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-2 text-purple-500 flex-shrink-0" />
                            <span>{event.dates?.display || event.dates?.start}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="w-4 h-4 mr-2 text-purple-500 flex-shrink-0" />
                            <span className="truncate">{event.venueName}, {event.city}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="w-4 h-4 mr-2 text-purple-500 flex-shrink-0" />
                            <span className="truncate">{event.organiserName}</span>
                          </div>
                        </div>
                        
                        <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                          {event.shortDescription}
                        </p>
                        
                        {/* Tags */}
                        <div className="flex flex-wrap gap-1 mb-3">
                          {(event.sectorTags || []).slice(0, 3).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        
                        <div className="flex items-center text-sm font-medium text-purple-600 group-hover:text-purple-700">
                          View details
                          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Subscribe CTA */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Never Miss a {data?.sector?.name} Event
          </h2>
          <p className="text-gray-600 mb-6">
            Subscribe to our calendar feed and get automatic updates when new events are added.
          </p>
          <Link to={`/subscribe/tag/${tagSlug}`}>
            <Button className="bg-purple-600 hover:bg-purple-700">
              <Calendar className="w-4 h-4 mr-2" />
              Subscribe to {data?.sector?.name} Events
            </Button>
          </Link>
        </div>
      </section>

      {/* Event Boost CTA */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Love {data?.sector?.name} Events?</p>
                <p className="text-sm text-gray-600">Share with your network and earn rewards!</p>
              </div>
            </div>
            <Link to="/boost/dashboard">
              <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white whitespace-nowrap">
                Join Event Boost
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Sectors */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Browse Other Sectors</h2>
          <div className="flex flex-wrap gap-3">
            {['technology', 'healthcare', 'events-industry', 'retail', 'manufacturing', 'hr'].filter(s => s !== tagSlug).map(sector => (
              <Link key={sector} to={`/events/sector/${sector}`}>
                <Badge variant="outline" className="px-4 py-2 hover:bg-purple-50 hover:border-purple-500 cursor-pointer">
                  {sector.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </Badge>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </MainLayout>
  );
}
